/**
 * Escape text for safe HTML insertion.
 */
export function escapeHtml(s: string): string {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Convert Nevisign export body (plain / light CM) into reader HTML
 * that DocxViewer can display (nr-p paragraphs).
 */
export function nevisignBodyToReaderHtml(
  body: string,
  opts?: { title?: string; dir?: "rtl" | "ltr" }
): string {
  const dir = opts?.dir ?? "rtl";
  const parts: string[] = [];
  if (opts?.title?.trim()) {
    parts.push(
      `<p class="nr-p nr-heading" dir="${dir}" style="font-weight:700;font-size:1.15em">${escapeHtml(
        opts.title.trim()
      )}</p>`
    );
  }

  const text = (body || "").replace(/\r\n/g, "\n").trim();
  if (!text) {
    parts.push(`<p class="nr-p" dir="${dir}">—</p>`);
    return parts.join("\n");
  }

  // Split on blank lines into paragraphs; preserve single newlines as <br/>
  const blocks = text.split(/\n{2,}/);
  for (const block of blocks) {
    const trimmed = block.trim();
    if (!trimmed) continue;

    // Simple GFM table → HTML table
    const lines = trimmed.split("\n");
    if (
      lines.length >= 2 &&
      /^\|/.test(lines[0]!) &&
      /^\|[\s-:|]+$/.test(lines[1]!.trim())
    ) {
      const parseRow = (line: string) =>
        line
          .replace(/^\|/, "")
          .replace(/\|$/, "")
          .split("|")
          .map((c) => escapeHtml(c.trim()));
      const headers = parseRow(lines[0]!);
      const rows = lines.slice(2).filter((l) => /^\|/.test(l)).map(parseRow);
      const thead = `<tr>${headers.map((h) => `<th>${h}</th>`).join("")}</tr>`;
      const tbody = rows
        .map((r) => `<tr>${r.map((c) => `<td>${c}</td>`).join("")}</tr>`)
        .join("");
      parts.push(
        `<table class="nr-table" dir="${dir}"><thead>${thead}</thead><tbody>${tbody}</tbody></table>`
      );
      continue;
    }

    // Light inline: **bold** then remaining *italic* / _italic_
    let html = escapeHtml(trimmed).replace(/\n/g, "<br/>");
    html = html.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
    html = html.replace(/\*([^*]+)\*/g, "<em>$1</em>");
    html = html.replace(/_([^_]+)_/g, "<em>$1</em>");

    parts.push(`<p class="nr-p" dir="${dir}">${html}</p>`);
  }

  return parts.join("\n");
}
